<template>
  <div>
    <el-form
      class="profile-form"
      :model="profileForm"
      label-width="190px"
      label-position="left"
      ref="profileForm"
      @submit.native.prevent
    >
      <div v-if="isProfileComplete">
        <empty>Profile Complete: All important sections of this profile are successfully completed!</empty>
      </div>
      <div v-else>
        <p class="info-text">Add more information to this profile</p>
      </div>

      <!-- Contact Information -->
      <div v-if="showContactInfo">
        <h3>
          <el-icon name="phone"></el-icon> Contact Information
        </h3>
        <el-form-item label="Email" v-if="!isFieldPresent('email_address')" :error="emailError">
          <el-input v-model="profileForm.email_address"
            @blur="validateEmail"
            @input="sanitizeInput('email_address')"/>
        </el-form-item>
        <el-form-item label="Mobile Number" v-if="!isFieldPresent('mobile_number')">
          <el-input v-model="profileForm.mobile_number"
          @input="profileForm.mobile_number = profileForm.mobile_number.replace(/[^0-9]/g, '');"
          @blur="handleInput('mobile_number')"/>
        </el-form-item>
      </div>

      <!-- Personal Information -->
      <div v-if="showPersonalInfo">
        <h3>
          <el-icon name="user"></el-icon> Personal Information
        </h3>
        <el-form-item label="Name" v-if="!isFieldPresent('name')">
          <el-input v-model="profileForm.name"
          @blur="handleInput('name')"
          @input="sanitizeInput('name')"/>
        </el-form-item>
        <el-form-item label="Gender" v-if="!isFieldPresent('gender')">
          <el-select v-model="profileForm.gender" placeholder="Select Gender">
            <el-option label="Male" value="Male"></el-option>
            <el-option label="Female" value="Female"></el-option>
            <el-option label="Other" value="Other"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Age" v-if="!isFieldPresent('age_range')">
          <el-select v-model="profileForm.age_range" placeholder="Select Age">
            <el-option label="Under 18" value="Under 18"></el-option>
            <el-option label="18-24" value="18-24"></el-option>
            <el-option label="25-34" value="25-34"></el-option>
            <el-option label="35-49" value="35-49"></el-option>
            <el-option label="50-64" value="50-64"></el-option>
            <el-option label="65+" value="65+"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="ID Number" v-if="!isFieldPresent('id_number')">
          <el-input v-model="profileForm.id_number"
          @input="sanitizeInput('id_number')"
          @blur="handleInput('id_number')"/>
        </el-form-item>
        <el-form-item label="Location" v-if="!isFieldPresent('location')">
          <el-input v-model="profileForm.location"
          @blur="handleInput('location')"
          @input="sanitizeInput('location')"/>
        </el-form-item>
      </div>

      <!-- Social Media -->
      <div v-if="showSocialInfo">
        <h3>
          <el-icon name="link"></el-icon> Social Media
        </h3>
        <el-form-item label="Twitter" v-if="!isFieldPresent('twitter_handle')">
          <el-input v-model="profileForm.twitter_handle"
          placeholder="@twitter_handle"
          @blur="handleAtSymbol('twitter_handle')"
          @input="sanitizeInput('twitter_handle')"/>
        </el-form-item>
        <el-form-item label="Instagram" v-if="!isFieldPresent('instagram_handle')">
          <el-input v-model="profileForm.instagram_handle"
          placeholder="@instagram_handle"
          @blur="handleAtSymbol('instagram_handle')"
          @input="sanitizeInput('instagram_handle')"/>
        </el-form-item>
        <el-form-item label="Facebook" v-if="!isFieldPresent('facebook_profile')">
          <el-input v-model="profileForm.facebook_profile"
          placeholder="Facebook username"
          @blur="handleInput('facebook_profile')"
          @input="sanitizeInput('facebook_profile')"/>
        </el-form-item>
      </div>

      <!-- Channels Permission -->
      <div>
        <h3>
          <i class="fa fa-exchange" aria-hidden="true"></i> Channels Optins and Permission
        </h3>
        <el-form-item v-for="(option, index) in initialOptins"
          :key="index" :label="option.channel" class="optin-permission">
          <el-tooltip
           v-if="!isOriginatorValid"
           placement="top"
           content="Mobile number is required for opt-in.">
            <el-switch v-model="option.opt_in" :disabled="!isOriginatorValid"
              @change="(value) => handleUpdateOptin(value, option)"></el-switch>
          </el-tooltip>
          <el-switch
            v-else
            v-model="option.opt_in"
            :disabled="!isOriginatorValid"
            @change="(value) => handleUpdateOptin(value, option)"
          ></el-switch>
        </el-form-item>
      </div>
    </el-form>

    <span class="footer">
      <div class="pull-right">
        <el-button
          v-if="!isProfileComplete"
          type="danger"
          size="small"
          class="ibox-tool-btn"
          @click="$emit('close')"
          plain
        >Cancel</el-button>
        <el-button
          v-if="!isProfileComplete"
          type="primary"
          size="small"
          class="ibox-tool-btn"
          :loading="savingProfile"
          :disabled="isFormEmpty && !detectChanges()"
          @click="handleFormSave"
          plain
        >Save</el-button>
        <el-button
          v-if="isProfileComplete"
          type="primary"
          size="small"
          class="ibox-tool-btn"
          @click="$emit('close')"
          plain
        >Got It</el-button>
      </div>
    </span>
  </div>
</template>

<script>
import Empty from '@/components/common/Empty';
import { mapState } from 'vuex';

export default {
  name: 'EnhanceProfileTab',
  components: {
    Empty,
  },
  data() {
    return {
      savingProfile: false,
      profileForm: {},
      emailError: '',
      initialOptins: [],
    };
  },
  mounted() {
    this.initialOptins = this.optins.map(optin => ({ ...optin }));
  },
  computed: {
    ...mapState('user', ['me']),
    isFormEmpty() {
      const isProfileEmpty = Object.values(this.profileForm).every(value => !value);
      return isProfileEmpty;
    },
    isProfileComplete() {
      return !this.showContactInfo && !this.showPersonalInfo && !this.showSocialInfo;
    },
    isOriginatorValid() {
      const mobileNumber = ((
        this.profileForm && this.profileForm.mobile_number) || this.initialMobileNumber || '').trim();
      const phoneRegex = /^(\+\d{1,3}\s?)?\d{9,}$/;
      return phoneRegex.test(mobileNumber);
    },
    showContactInfo() {
      return this.showInfo(['email_address', 'mobile_number']);
    },
    showPersonalInfo() {
      return this.showInfo(['name', 'gender', 'age_range', 'id_number', 'location']);
    },
    showSocialInfo() {
      return this.showInfo(['twitter_handle', 'facebook_profile', 'instagram_handle']);
    },
  },
  methods: {
    showInfo(fields) {
      return fields.some(field => !this.isFieldPresent(field));
    },
    isFieldPresent(field) {
      return this.demographics.some(item => item.field === field);
    },
    requiredOptins() {
      return this.initialOptins.map(optin => optin.channel);
    },
    detectChanges() {
      let changesDetected = false;
      this.initialOptins.forEach((initialOptin, index) => {
        if (initialOptin.opt_in !== this.optins[index].opt_in) {
          changesDetected = true;
        }
      });
      return changesDetected;
    },
    handleUpdateOptin(status, option) {
      const channel = option.channel === 'SMS' ? 'Message' : option.channel;
      const company = {
        id: this.me.use_as_company,
        company_name: this.me.company_name,
      };

      this.isLoading = true;

      const payload = {
        originator: this.initialMobileNumber,
        company: company.id,
        campaign: '',
        channel_type: channel.toLowerCase(),
        optin: status,
        permission: status,
        age: status,
      };
      this.$store.dispatch('privacy/createOptins', { company, payload })
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.$notify({
            type: 'info',
            message: 'Something went wrong. We are working on it.',
          });
        });
    },
    handleInput(field) {
      if (this.profileForm[field]) {
        this.profileForm[field] = this.profileForm[field].trim();
      }
    },
    handleAtSymbol(field) {
      if (this.profileForm[field]) {
        this.profileForm[field] = this.profileForm[field].trim().replace(/^@/, '');
      }
    },
    sanitizeInput(fieldName) {
      this.profileForm[fieldName] = this.profileForm[fieldName].replace(/'/g, '');
    },
    validateEmail() {
      this.handleInput('email_address');
      const email = (this.profileForm.email_address || '').trim();
      if (email.length === 0) {
        this.emailError = '';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = 'Please enter a valid email address';
      } else {
        this.emailError = '';
      }
    },
    handleFormSave() {
      this.$refs.profileForm.validate(valid => {
        if (valid) {
          const filteredProfileForm = {};
          Object.keys(this.profileForm).forEach(field => {
            const value = this.profileForm[field];

            if (typeof value === 'string' && value.trim() !== '') {
              filteredProfileForm[field] = value;
            } else if (typeof value === 'boolean') {
              filteredProfileForm[field] = value;
            }
          });
          return this.updateProfile(filteredProfileForm);
        }
        return false;
      });
    },
    addCategoryToProfileForm(profileForm) {
      const categorizedForm = {};

      Object.entries(profileForm).forEach(([field, value]) => {
        const category = this.getNuggetCategory(field);
        categorizedForm[field] = { category, value };
      });
      return categorizedForm;
    },
    updateProfile(filteredForm) {
      const profileOptins = this.requiredOptins();
      const filteredKeys = Object.keys(filteredForm);

      const hasAnyOptin = profileOptins.some(optin => filteredKeys.includes(optin));
      const isOnlyOptins = hasAnyOptin && filteredKeys.every(key => profileOptins.includes(key));

      if (isOnlyOptins) {
        this.$emit('updateOptins');
      } else {
        const updatedForm = { ...filteredForm };
        profileOptins.forEach(optin => delete updatedForm[optin]);

        this.$emit('updateProfile', {
          profileForm: JSON.stringify(this.addCategoryToProfileForm(updatedForm)),
        });
      }
    },
  },
  props: {
    demographics: {
      type: Array,
      default: () => [],
    },
    optins: {
      type: Array,
      default: () => [],
    },
    initialMobileNumber: {
      type: String,
      required: true,
    },
    getNuggetCategory: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-form {
  margin-bottom: 40px;
}
.info-text {
  margin-bottom: 30px;
}
.optin-permission {
  margin-bottom: 0px;
}
</style>



// WEBPACK FOOTER //
// EnhanceProfileForm.vue?1fdd17e6